/**
 * React Native for Web primitives with Tailwind styling.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * react-native-web's `View`/`Text` resolve their own class name from the RN
 * `style` prop and then *overwrite* `domProps.className` — a Tailwind class
 * passed to `<View className="…">` is silently dropped (verified against
 * react-native-web 0.19.13).
 *
 * `unstable_createElement` is RNW's sanctioned escape hatch for rendering a DOM
 * element through its own prop pipeline; it preserves `className` while still
 * applying RNW's accessibility prop mapping and style resolution. Every
 * primitive below goes through it, so the app renders through React Native for
 * Web and is styled entirely with Tailwind.
 *
 * Genuine RN components (ActivityIndicator) are used directly where their
 * behaviour, not their styling, is what we need.
 */
import { type ReactNode, forwardRef } from 'react';
import { unstable_createElement } from 'react-native-web';

type DomProps = Record<string, unknown>;

/**
 * Style values are resolved by react-native-web's StyleSheet, so this is the RN
 * style vocabulary (a subset of CSS, camelCased). Reserve it for values that
 * cannot be expressed as a Tailwind class — chiefly the runtime lifecycle
 * colours, which come from CSS custom properties.
 */
export type RnStyle = Record<string, string | number | undefined>;

function el(tag: string, props: DomProps): JSX.Element {
  return unstable_createElement(tag, props) as JSX.Element;
}

export interface BoxProps {
  className?: string;
  children?: ReactNode;
  id?: string;
  role?: string;
  style?: RnStyle;
  onClick?: (e: React.MouseEvent) => void;
  'aria-label'?: string;
  'aria-live'?: 'polite' | 'assertive' | 'off';
  'aria-hidden'?: boolean;
  'aria-expanded'?: boolean;
  title?: string;
  as?: 'div' | 'section' | 'header' | 'main' | 'nav' | 'ul' | 'li' | 'article' | 'aside' | 'footer';
}

/** Block-level container. Replaces RN `View`. */
export const Box = forwardRef<HTMLDivElement, BoxProps>(function Box({ as = 'div', children, ...rest }, ref) {
  return el(as, { ...rest, ref, children });
});

export interface TxtProps {
  className?: string;
  children?: ReactNode;
  title?: string;
  id?: string;
  style?: RnStyle;
  as?: 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'label' | 'dt' | 'dd';
  htmlFor?: string;
  'aria-hidden'?: boolean;
  'aria-live'?: 'polite' | 'assertive' | 'off';
}

/** Inline or block text. Replaces RN `Text`. */
export function Txt({ as = 'span', children, ...rest }: TxtProps): JSX.Element {
  return el(as, { ...rest, children });
}

export interface BtnProps {
  className?: string;
  children?: ReactNode;
  onPress?: () => void;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
  style?: RnStyle;
  'aria-label'?: string;
  'aria-pressed'?: boolean;
  'aria-expanded'?: boolean;
}

/**
 * Pressable control. Renders a real `<button>` so keyboard activation, focus
 * order and screen-reader semantics come for free.
 */
export const Btn = forwardRef<HTMLButtonElement, BtnProps>(function Btn(
  { children, onPress, onHoverIn, onHoverOut, type = 'button', ...rest },
  ref,
) {
  return el('button', {
    ...rest,
    ref,
    type,
    onClick: onPress,
    onMouseEnter: onHoverIn,
    onMouseLeave: onHoverOut,
    // Keyboard users get the same affordance as pointer users.
    onFocus: onHoverIn,
    onBlur: onHoverOut,
    children,
  });
});

export interface FieldProps {
  className?: string;
  value: string;
  onChangeText: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'search' | 'date' | 'password' | 'email';
  id?: string;
  max?: string;
  min?: string;
  'aria-label'?: string;
  autoComplete?: string;
  inputMode?: 'text' | 'search' | 'numeric';
}

/** Text input. Replaces RN `TextInput`. */
export function Field({ onChangeText, type = 'text', ...rest }: FieldProps): JSX.Element {
  return el('input', {
    ...rest,
    type,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChangeText(e.target.value),
  });
}

export interface SelectProps {
  className?: string;
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<{ value: string; label: string }>;
  id?: string;
  'aria-label'?: string;
}

export function Select({ onChange, options, ...rest }: SelectProps): JSX.Element {
  return el('select', {
    ...rest,
    onChange: (e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value),
    children: options.map((o) => el('option', { key: o.value, value: o.value, children: o.label })),
  });
}

/** Horizontally scrollable region — the orders table on small screens. */
export function ScrollX({ className = '', children }: { className?: string; children: ReactNode }): JSX.Element {
  return el('div', { className: `scroll-x ${className}`, children });
}
